import { getCoverTranslateBounds } from './coverBounds';

/**
 * cropper-image is transformed around its own center (the CSS default `transform-origin`), not
 * its top-left corner. So its rendered left/top edge sits at
 * `naturalSize * (1 - scale) / 2 + translate` relative to the canvas's own origin — not just
 * `translate` — and every conversion between the transform's translate and a natural-pixel crop
 * offset needs that same correction term, or it's off by however far `scale` is from 1.
 */

/** Natural-pixel offset of the canvas's visible edge into the source image, for one axis. */
export function translateToNaturalOffset(translate: number, naturalSize: number, scale: number): number {
	return -((naturalSize * (1 - scale)) / 2 + translate) / scale;
}

/** Inverse of {@link translateToNaturalOffset} — the transform's translate for a given crop offset. */
export function naturalOffsetToTranslate(offset: number, naturalSize: number, scale: number): number {
	return -offset * scale - (naturalSize * (1 - scale)) / 2;
}

/**
 * The cover-clamped version of a proposed cropper-image transform `matrix`, or `undefined` when it
 * already keeps the image fully covering a `canvasWidth` x `canvasHeight` canvas (or isn't one
 * that can be clamped). The matrix's scale/skew are kept as-is; only its translate is clamped.
 */
export function clampMatrixToCover(
	matrix: readonly number[],
	naturalSize: { width: number; height: number },
	canvasWidth: number,
	canvasHeight: number,
): number[] | undefined {
	const [scale, b, c, d, translateX, translateY] = matrix;
	if (!(scale > 0)) return undefined;

	const [minX, maxX] = getCoverTranslateBounds(naturalSize.width, scale, canvasWidth);
	const [minY, maxY] = getCoverTranslateBounds(naturalSize.height, scale, canvasHeight);
	const clampedX = Math.min(maxX, Math.max(minX, translateX));
	const clampedY = Math.min(maxY, Math.max(minY, translateY));

	return clampedX === translateX && clampedY === translateY ? undefined : [scale, b, c, d, clampedX, clampedY];
}

/**
 * cropper-image's own `$moveTo(x, y)` does not set the transform's translate to `(x, y)`
 * directly — it divides by the *current* scale first (`translate = x / scale`), unlike `$move`,
 * whose relative delta happens to cancel that division out via matrix composition. Multiplying
 * the desired translate by `scale` before calling `$moveTo` is what cancels it back out, so the
 * transform ends up with the translate actually intended (verified against the real,
 * unmocked library — passing a target translate to `$moveTo` directly leaves the image off by a
 * factor of `1 / scale`, which is wildly wrong for any non-1:1 image and was landing the image
 * entirely outside the visible canvas).
 */
export function moveImageTo(
	imageElement: { $moveTo: (x: number, y: number) => unknown },
	translateX: number,
	translateY: number,
	scale: number,
): void {
	imageElement.$moveTo(translateX * scale, translateY * scale);
}
